import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AimieRobotAvatarIcon } from '../chat/ChatAvatarIcons';
import './SkillUsedReasoningCard.css';

const POPUP_GAP = 9;
const VIEWPORT_MARGIN = 12;
const HOVER_HANDOFF_MS = 120;

function isAnchorVisibleInViewport(rect, viewport) {
  return (
    rect.bottom > viewport.top
    && rect.top < viewport.top + viewport.height
    && rect.right > viewport.left
    && rect.left < viewport.left + viewport.width
  );
}

function SkillUsedReasoningCard({ children, explanation, codeCount = 1, theme = 'dark' }) {
  const [anchor, setAnchor] = useState(null);
  const [position, setPosition] = useState(null);
  const closeTimeoutRef = useRef(null);
  const tooltipId = `skill-used-reasoning-${useId().replace(/:/g, '')}`;
  const hasExplanation = Boolean(String(explanation || '').trim());

  const cancelScheduledClose = useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const close = useCallback(() => {
    cancelScheduledClose();
    setAnchor(null);
  }, [cancelScheduledClose]);

  const scheduleClose = useCallback(() => {
    cancelScheduledClose();
    closeTimeoutRef.current = window.setTimeout(() => {
      closeTimeoutRef.current = null;
      setAnchor(null);
    }, HOVER_HANDOFF_MS);
  }, [cancelScheduledClose]);

  useEffect(() => () => cancelScheduledClose(), [cancelScheduledClose]);

  useLayoutEffect(() => {
    if (!anchor) {
      setPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const rect = anchor.getBoundingClientRect();
      const visualViewport = window.visualViewport;
      const viewport = {
        left: visualViewport?.offsetLeft || 0,
        top: visualViewport?.offsetTop || 0,
        width: visualViewport?.width || window.innerWidth,
        height: visualViewport?.height || window.innerHeight,
      };
      if (!isAnchorVisibleInViewport(rect, viewport)) {
        close();
        return;
      }
      const width = Math.min(rect.width, viewport.width - (VIEWPORT_MARGIN * 2));
      setPosition({
        top: rect.bottom + POPUP_GAP,
        width,
        left: Math.min(
          Math.max(
            rect.left + (rect.width / 2),
            viewport.left + VIEWPORT_MARGIN + (width / 2),
          ),
          viewport.left + viewport.width - VIEWPORT_MARGIN - (width / 2),
        ),
      });
    };

    updatePosition();
    const resizeObserver = new ResizeObserver(updatePosition);
    resizeObserver.observe(anchor);
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) close();
    });
    intersectionObserver.observe(anchor);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.visualViewport?.addEventListener('resize', updatePosition);
    window.visualViewport?.addEventListener('scroll', updatePosition);
    return () => {
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      window.visualViewport?.removeEventListener('resize', updatePosition);
      window.visualViewport?.removeEventListener('scroll', updatePosition);
    };
  }, [anchor, close]);

  const open = (event) => {
    if (!hasExplanation) {
      close();
      return;
    }
    cancelScheduledClose();
    setAnchor(event.currentTarget);
  };

  return (
    <>
      <div
        className="analytics-card skill-used-reasoning-card"
        tabIndex={hasExplanation ? 0 : undefined}
        aria-describedby={anchor ? tooltipId : undefined}
        onMouseEnter={open}
        onMouseOver={open}
        onMouseLeave={scheduleClose}
        onFocus={open}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            scheduleClose();
          }
        }}
      >
        {children}
      </div>
      {anchor && position && createPortal(
        <div
          id={tooltipId}
          className={`skill-used-reasoning-popup skill-used-reasoning-popup--${theme === 'dark' ? 'dark' : 'light'}`}
          style={{ top: position.top, left: position.left, width: position.width }}
          role="tooltip"
          onMouseEnter={cancelScheduledClose}
          onMouseLeave={scheduleClose}
        >
          <div className="skill-used-reasoning-popup__message">
            <span className="skill-used-reasoning-popup__avatar" aria-hidden="true">
              <AimieRobotAvatarIcon />
            </span>
            <div>
              <strong>
                {codeCount > 1
                  ? 'Why this response received these codes'
                  : 'Why this response received this code'}
              </strong>
              <p>{explanation}</p>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

export default SkillUsedReasoningCard;
export { isAnchorVisibleInViewport };
